import React from "react";
import SlipMark from "../assets/slip-mark-light.svg";
import SlipMarkDark from "../assets/slip-mark-dark.svg";
import ThemeToggle from "../components/ThemeToggle";
import menu from "../assets/menu.svg";
import { trackEvent } from "../utils/clarity";
import {
    FaUsers,
    FaExchangeAlt,
    FaVideo,
    FaCode,
    FaTshirt,
    FaCut,
    FaCamera,
    FaExpand,
    FaPaintBrush
} from 'react-icons/fa';
import { FaWandMagicSparkles } from "react-icons/fa6";
import HeroProof from "../components/HeroProof";

// Constants
const LINKS = {
    PLAYGROUND: 'https://playground.tryonlabs.ai',
    DOCS: 'https://docs.tryonlabs.ai',
    GITHUB: 'https://github.com/tryonlabs/',
    DEVELOPERS: '/developers'
};

const SEO = {
    TITLE: 'TryOn AI - Virtual Try-On, Model Swap & Fashion Image Tools',
    DESCRIPTION: 'Virtual try-on, model swap, reimagine, image to video and studio tools for fashion, in one playground and one API. Your collection, photographed without the studio.',
    OG_TITLE: 'TryOn AI - Your collection, photographed. Without the studio.',
    OG_DESCRIPTION: 'Virtual try-on, model swap, reimagine, image to video and studio tools for fashion, in one playground and one API.',
    OG_IMAGE: 'https://tryonlabs.ai/og-image.png'
};

const ANALYTICS = {
    CATEGORY: 'CTA',
    EVENTS: {
        HERO_CTA: 'hero_cta',
        CAPABILITIES_CTA: 'capabilities_cta',
        USE_CASE_CTA: 'use_case_cta'
    }
};

// Analytics tracking function
const trackCTAClick = (action, label) => {
    // Clarity event tracking
    trackEvent('CTA Click', {
        action,
        label,
        category: ANALYTICS.CATEGORY
    });
};

// Loading state hook
const useLoadingState = () => {
    const [isLoading, setIsLoading] = React.useState(false);
    
    const handleExternalLink = (url, action, label) => {
        setIsLoading(true);
        trackCTAClick(action, label);
        
        // Simulate loading time for better UX
        setTimeout(() => {
            window.open(url, '_blank');
            setIsLoading(false);
        }, 300);
    };
    
    return { isLoading, handleExternalLink };
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-ground flex items-center justify-center">
                    <div className="text-center p-8">
                        <div className="text-ink text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-ink mb-4">Something went wrong</h2>
                        <p className="text-muted mb-6">We're working to fix this issue. Please try refreshing the page.</p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-action text-white rounded-full hover:bg-action-hover transition-colors shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const Navbar = () => {
    return (
        <nav role="navigation" aria-label="Main navigation" className="fixed top-0 z-40 bg-ground h-auto w-screen sm:w-full border-b border-hairline">
            <div className="mx-auto max-w-7xl flex items-center justify-between p-2 sm:px-4">
                <div className="flex items-center mr-6">
                    <div className="flex items-center cursor-pointer" onClick={() => window.location.href = "/"}>
                        <img className="nav-menu w-6 h-6 object-contain cursor-pointer mr-2 md:hidden" src={menu} alt="menu" loading="lazy" />
                        <img className="mark-on-light w-8 h-8 shrink-0 object-contain" src={SlipMark} alt="" loading="lazy" />
                        <img className="mark-on-dark w-8 h-8 shrink-0 object-contain" src={SlipMarkDark} alt="" loading="lazy" />
                        <span className="hidden md:block ml-3 text-xl font-bold text-ink">TryOn AI</span>
                    </div>
                </div>

                <div className="flex items-center justify-center whitespace-nowrap md:w-max pointer-events-auto gap-0.5 sm:gap-2 md:gap-3">
                    <ThemeToggle />
                    <a target="_blank" href={LINKS.GITHUB} rel="noopener noreferrer" className="hidden md:flex items-center justify-center w-10 h-8 text-muted hover:text-ink transition-colors">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                    </a>
                    <a href={LINKS.DEVELOPERS}>
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            For Developers
                        </button>
                    </a>
                    <a target="_blank" href={LINKS.DOCS} rel="noopener noreferrer">
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Docs
                        </button>
                    </a>
                    <a target="_blank" href="https://playground.tryonlabs.ai/pricing" rel="noopener noreferrer">
                        <button className="hidden sm:flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Pricing
                        </button>
                    </a>
                    <a href="https://playground.tryonlabs.ai/credits" target="_blank" rel="noopener noreferrer">
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-white bg-action rounded-full hover:bg-action-hover transition-colors shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                            Get API Access
                        </button>
                    </a>
                </div>
            </div>
        </nav>
    );
};

const HeroSection = () => {
    const { isLoading, handleExternalLink } = useLoadingState();
    
    return (
    <section aria-labelledby="hero-heading" className="w-full py-20 md:py-28 bg-ground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
                {/* Left Column - Text Content */}
                <div className="text-center lg:text-left space-y-8 animate-fade-in-up">
                    {/* Badge */}
                    <div className="inline-flex items-center px-4 py-2 rounded-full border bg-tray border-hairline">
                        <span className="text-ink text-sm font-medium">Virtual try-on, live now</span>
                    </div>

                    {/* Main Headline */}
                    <div className="space-y-4">
                        <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-newsreader font-medium text-ink leading-[1.1]">
                            Your collection, photographed.
                            <span className="block">
                                Without the studio.
                            </span>
                        </h1>
                    </div>

                    {/* Subtitle */}
                    <div className="space-y-6 max-w-2xl mx-auto lg:mx-0">
                        <p className="text-lg sm:text-xl text-muted leading-relaxed">
                            See garments on people, swap models, reimagine pictures, make short clips and finish the shot, all in one playground.
                        </p>
                        <p className="text-lg sm:text-xl text-ink font-semibold leading-relaxed">
                            Try it in the playground, or build it into your app with the API.
                        </p>
                    </div>
                            
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 pt-4">
                        <button 
                            onClick={() => handleExternalLink(LINKS.PLAYGROUND, 'hero_cta', 'Launch Playground')}
                            disabled={isLoading}
                            className="group flex items-center justify-center lg:px-8 lg:py-4 py-3 px-6 lg:text-base text-xs font-semibold text-white bg-action rounded-full hover:bg-action-hover transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    Launch Playground
                                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </>
                            )}
                        </button>
                        <a href={LINKS.DEVELOPERS}>
                            <button className="flex items-center justify-center lg:px-8 lg:py-4 py-3 px-6 lg:text-base text-xs font-medium text-coral border border-coral-line rounded-full hover:bg-tray transition-all duration-300 bg-sheet">
                                For Developers
                            </button>
                        </a>
                    </div>
                </div>

                {/* Right Column - Hero concept */}
                <div className="hidden lg:block">
                    <HeroProof />
                </div>
            </div>
        </div>
        
    </section>
    );
};

const CAPABILITIES = [
    { Icon: FaTshirt, title: 'Virtual Try-On', desc: 'See a garment on a person, from a flat photo or a worn one.', points: ['Fast, balanced and quality modes', 'Up to 4K output', 'Keeps garment details'] },
    { Icon: FaExchangeAlt, title: 'Model Swap', desc: 'Keep the outfit and change the person wearing it.', points: ['Preserves the garment', 'Keeps pose and lighting', 'Range of sizes and skin tones'] },
    { Icon: FaUsers, title: 'Model Generator', desc: 'Create a new fashion model from a description, with a guided prompt builder.', points: ['Custom look and pose', 'Diverse demographics', 'High-resolution output'] },
    { Icon: FaPaintBrush, title: 'Reimagine', desc: 'Change the look, mood or setting of a picture you already have.', points: ['Start from your own photo', 'Prompt gallery for ideas', 'Keep the best versions'] },
    { Icon: FaCamera, title: 'Generate Images', desc: 'Make a picture from a description of the shot you want.', points: ['Text to image', 'Fashion prompt gallery', 'Saved to your gallery'] },
    { Icon: FaVideo, title: 'Image to Video', desc: 'Turn a still into a short clip for social and product pages.', points: ['8-second clips', 'Up to 1080p', 'Starts from any image'] },
    { Icon: FaCut, title: 'Background Remove & Change', desc: 'Remove a background or place the product in a new setting.', points: ['Clean cutouts', 'Studio and location backdrops', 'Edge detail preserved'] },
    { Icon: FaWandMagicSparkles, title: 'Image Enhancement', desc: 'Upscale and finish pictures so they are ready to publish.', points: ['Upscale up to 4K', 'Resize and crop', 'Detail preserved'] },
    { Icon: FaExpand, title: 'Studio', desc: 'One place to finish a picture: backgrounds, upscale, crop, caption and chat edits.', points: ['Edit by chatting', 'Auto captions', 'All your tools in one view'] },
];

const CapabilitiesOverview = () => (
    <section id="capabilities" aria-labelledby="capabilities-heading" className="w-full py-24 md:py-32 bg-sheet">
        <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-20">
                <h2 id="capabilities-heading" className="text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight">
                    Everything live
                    <span className="block text-muted mt-2">in the playground</span>
                </h2>
                <p className="text-xl text-muted max-w-3xl mx-auto leading-relaxed">
                    The tools our team and customers use today, from try-on to finished pictures and short clips.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {CAPABILITIES.map((c) => (
                <div key={c.title} className="bg-sheet rounded-2xl p-8 border border-hairline transition-colors group">
                    <div className="w-16 h-16 bg-tray rounded-2xl flex items-center justify-center mb-6 group-hover:bg-tray-strong transition-colors">
                        <c.Icon className="w-8 h-8 text-ink" />
                    </div>
                    <h3 className="text-xl font-bold text-ink mb-4">{c.title}</h3>
                    <p className="text-muted mb-6 leading-relaxed">{c.desc}</p>
                    <ul className="text-sm text-muted space-y-1">
                        {c.points.map((pt) => <li key={pt}>✓ {pt}</li>)}
                    </ul>
                </div>
                ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-16">
                <a href={LINKS.PLAYGROUND} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('capabilities_cta', 'Try All Capabilities Now')}>
                    <button className="lg:px-8 lg:py-4 py-3 px-6 lg:text-base text-xs text-white bg-action rounded-full hover:bg-action-hover transition-colors font-semibold shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                        Try All Capabilities Now
                    </button>
                </a>
            </div>
        </div>
    </section>
);

const DeveloperSection = () => (
    <section aria-labelledby="developer-heading" className="w-full py-24 md:py-32 bg-ground">
        <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 id="developer-heading" className="text-4xl md:text-5xl font-bold text-ink mb-6 leading-tight">
                Building an app?
            </h2>
            <p className="text-xl text-muted max-w-3xl mx-auto leading-relaxed mb-10">
                The same tools are available over an HTTP API, with documentation, SDKs and open-source projects to get you started.
            </p>
            <a href={LINKS.DEVELOPERS}>
                <button className="group inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white rounded-full transition-all duration-300 bg-action hover:bg-action-hover shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                    Explore Developer Resources
                </button>
            </a>
        </div>
    </section>
);

const AUDIENCES = [
    { Icon: FaUsers, title: 'Fashion brands & retailers', desc: 'Put products on models, change the person or the backdrop, and make short clips without booking a studio.' },
    { Icon: FaCode, title: 'Developers & startups', desc: 'Add try-on, model swap and image tools to your own app through the API, and test each call in the playground first.' },
];

const TargetAudienceSection = () => (
    <div className="w-full py-24 md:py-32 bg-sheet border-t border-hairline">
        <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight">Who uses TryOn AI</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {AUDIENCES.map(({ Icon, title, desc }) => (
                    <div key={title} className="bg-ground rounded-2xl p-8 border border-hairline">
                        <div className="w-14 h-14 bg-tray rounded-xl flex items-center justify-center mb-6">
                            <Icon className="w-7 h-7 text-ink" />
                        </div>
                        <h3 className="text-2xl font-bold text-ink mb-3">{title}</h3>
                        <p className="text-muted leading-relaxed">{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const USE_CASES = [
    { tag: 'Product photos', title: 'Show products on people', desc: 'Upload a garment photo and see it worn, then swap the model to cover more sizes and looks.' },
    { tag: 'Content creation', title: 'Make social content', desc: 'Turn stills into short clips and place products in new settings for posts and campaigns.' },
    { tag: 'Finishing', title: 'Get pictures ready to publish', desc: 'Remove or change backgrounds, upscale, crop and caption in Studio.' },
];

const UseCasesSection = () => (
    <div className="w-full py-24 md:py-32 bg-ground border-t border-hairline">
        <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight">What you can make</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {USE_CASES.map(({ tag, title, desc }) => (
                    <div key={title} className="bg-sheet rounded-2xl p-8 border border-hairline">
                        <div className="inline-flex items-center px-3 py-1 bg-tray rounded-full border mb-6 border-hairline">
                            <span className="text-ink text-xs font-semibold">{tag}</span>
                        </div>
                        <h3 className="text-xl font-bold text-ink mb-3">{title}</h3>
                        <p className="text-muted leading-relaxed">{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);


// Final CTA Section
const FinalCTASection = () => (
    <div className="w-full py-20 md:py-24 bg-ground">
        <div className="max-w-4xl mx-auto px-6">
            {/* Main Content */}
            <div className="text-center lg:mb-12 mb-8">
                <h2 className="text-4xl md:text-5xl font-bold text-ink mb-6 leading-tight">
                    Ready to Get Started?
                </h2>
                <p className="text-lg md:text-xl text-muted leading-relaxed max-w-2xl mx-auto">
                    Try every tool in the playground, then add them to your app through the API.
                </p>
            </div>
            
            {/* Primary CTA */}
            <div className="text-center lg:mb-12 mb-8">
                <a href="https://playground.tryonlabs.ai" target="_blank" rel="noopener noreferrer">
                    <button className="inline-flex items-center justify-center lg:px-12 lg:py-5 py-3 px-6 lg:text-xl text-base font-semibold text-white rounded-full transform transition-all duration-200 bg-action hover:bg-action-hover shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                        Launch Playground Now
                        <svg className="w-6 h-6 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </button>
                </a>
            </div>

            {/* Secondary Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                <a href="https://playground.tryonlabs.ai/credits" target="_blank" rel="noopener noreferrer" className="group">
                    <button className="flex items-center justify-center px-6 py-3 text-coral border border-coral-line rounded-full transition-all duration-200 group-hover:bg-tray bg-sheet hover:bg-tray">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Get API Access
                    </button>
                </a>
                <a href="https://docs.tryonlabs.ai" target="_blank" rel="noopener noreferrer" className="group">
                    <button className="flex items-center justify-center px-6 py-3 text-coral border border-coral-line rounded-full transition-all duration-200 group-hover:bg-tray bg-sheet hover:bg-tray">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Documentation
                    </button>
                </a>
            </div>

        </div>
    </div>
);

// Footer Component
const Footer = () => (
    <div className="w-full py-16 bg-ground border-t border-hairline">
        <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">TryOn AI</h4>
                    <p className="text-muted text-sm leading-relaxed mb-4">
                        Your collection, photographed.<br/> Without the studio.
                    </p>
                    <div className="flex space-x-4">
                        <a href="mailto:contact@tryonlabs.ai" className="text-muted hover:text-coral transition-colors">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M3 8l9 6 9-6v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                                <path d="M21 6L12 13 3 6h18z"/>
                            </svg>
                        </a>
                    </div>
                </div>

                {/* Playground */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Playground</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="https://playground.tryonlabs.ai" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Live Playground</a></li>
                        <li><a href="https://playground.tryonlabs.ai/pricing" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Pricing</a></li>
                        <li><a href="https://docs.tryonlabs.ai" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Documentation</a></li>
                        <li><a href="mailto:support@tryonlabs.ai" className="text-muted hover:text-coral transition-colors">Support</a></li>
                    </ul>
                </div>

                {/* APIs */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">APIs</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="https://docs.tryonlabs.ai/api-playground/virtual-try-on" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Virtual Try-On</a></li>
                        <li><a href="https://docs.tryonlabs.ai/outfit-generation" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Outfit Generation</a></li>
                        <li><a href="https://docs.tryonlabs.ai/outfit-captioning" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Outfit Captioning</a></li>
                    </ul>
                </div>

                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Company</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="/" className="text-muted hover:text-coral transition-colors">Home</a></li>
                        <li><a href="https://github.com/tryonlabs/" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Open Source</a></li>
                        <li><a href="mailto:contact@tryonlabs.ai?subject=Partnership" className="text-muted hover:text-coral transition-colors">Partnerships</a></li>
                        <li><a href="/contact-us" className="text-muted hover:text-coral transition-colors">Contact Us</a></li>
                        <li><a href="/terms-and-conditions" className="text-muted hover:text-coral transition-colors">Terms & Conditions</a></li>
                        <li><a href="/cancellation-and-refund-policy" className="text-muted hover:text-coral transition-colors">Cancellation & Refund Policy</a></li>
                    </ul>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="pt-8 border-t border-hairline flex flex-col md:flex-row justify-between items-center">
                <p className="text-muted text-sm">
                    &copy; {new Date().getFullYear()} TryOn AI. All rights reserved.
                </p>
            </div>
        </div>
    </div>
);

export default function PlaygroundPage() {
    // SEO Meta Tags
    React.useEffect(() => {
        // Add custom CSS animations
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            .animate-fade-in-up {
                animation: fadeInUp 0.6s ease-out;
            }
            
            .animate-fade-in-up-delay-1 {
                animation: fadeInUp 0.6s ease-out 0.1s both;
            }
            
            .animate-fade-in-up-delay-2 {
                animation: fadeInUp 0.6s ease-out 0.2s both;
            }
            
            .animate-fade-in-up-delay-3 {
                animation: fadeInUp 0.6s ease-out 0.3s both;
            }
            
            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.8;
                }
            }
            
            .animate-pulse-slow {
                animation: pulse 2s ease-in-out infinite;
            }
        `;
        document.head.appendChild(style);
        
        // Update page title
        document.title = SEO.TITLE;
        
        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', SEO.DESCRIPTION);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'description';
            meta.content = SEO.DESCRIPTION;
            document.head.appendChild(meta);
        }
        
        // Add Open Graph tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
            const ogTitleMeta = document.createElement('meta');
            ogTitleMeta.setAttribute('property', 'og:title');
            ogTitleMeta.content = SEO.OG_TITLE;
            document.head.appendChild(ogTitleMeta);
        }
        
        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (!ogDescription) {
            const ogDescMeta = document.createElement('meta');
            ogDescMeta.setAttribute('property', 'og:description');
            ogDescMeta.content = SEO.OG_DESCRIPTION;
            document.head.appendChild(ogDescMeta);
        }
        
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
            const ogImageMeta = document.createElement('meta');
            ogImageMeta.setAttribute('property', 'og:image');
            ogImageMeta.content = SEO.OG_IMAGE;
            document.head.appendChild(ogImageMeta);
        }
        
        // Add Twitter Card tags
        const twitterCard = document.querySelector('meta[name="twitter:card"]');
        if (!twitterCard) {
            const twitterCardMeta = document.createElement('meta');
            twitterCardMeta.name = 'twitter:card';
            twitterCardMeta.content = 'summary_large_image';
            document.head.appendChild(twitterCardMeta);
        }
        
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (!twitterTitle) {
            const twitterTitleMeta = document.createElement('meta');
            twitterTitleMeta.name = 'twitter:title';
            twitterTitleMeta.content = SEO.OG_TITLE;
            document.head.appendChild(twitterTitleMeta);
        }
        
        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (!twitterDescription) {
            const twitterDescMeta = document.createElement('meta');
            twitterDescMeta.name = 'twitter:description';
            twitterDescMeta.content = SEO.OG_DESCRIPTION;
            document.head.appendChild(twitterDescMeta);
        }
    }, []);
    
    return (
        <ErrorBoundary>
            <div className="h-full overflow-hidden w-full bg-ground">
                <Navbar />
                <div className="h-full scrollbar items-center justify-start bg-ground">
                    <HeroSection />
                    <CapabilitiesOverview />
                    <DeveloperSection />
                    <TargetAudienceSection />
                    <UseCasesSection />
                    <FinalCTASection />
                    <Footer />
                </div>
            </div>
        </ErrorBoundary>
    );
}
